import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import {
  TicketStatus,
  ADMIN_ONLY_STATUSES,
  ViewType,
  statusesForCategory,
  statusLabel,
  STATUSES_REQUIRING_TRACKING_NUMBER
} from '@/types/domain';
import { ApiError } from '@/lib/errors';
import { useToasts } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';
import { useTagTeammates } from './useTagTeammates';

interface Props {
  ticketId: string;
  ticketCategory: string;
  currentStatus: TicketStatus;
  open: boolean;
  onClose: () => void;
}

/**
 * V4.9.8 — Change Status modal is now both category-aware AND role-aware.
 *
 *   • Category-aware: for an A-ticket the dropdown shows A-flow statuses
 *     (12 options). For a B-ticket it shows B-flow statuses (7 options).
 *     No more confused users picking "Approved reprint" on a Trade-show
 *     booth quote ticket.
 *
 *   • Role-aware: the 4 "Approved-*" statuses are Admin-only. Non-Admin
 *     users (Sales Lead, Communicator, Agent, Specialist) never see them
 *     in the dropdown. Even if they craft a request bypassing the UI,
 *     the backend re-validates and returns 403 — this is defence in depth.
 *
 * This is the systemic fix for the V4.9.7 production incident where
 * Aaron (Communicator) flipped a ticket to "Approved reprint" through
 * the dropdown.
 */
export function ChangeStatusModal({
  ticketId,
  ticketCategory,
  currentStatus,
  open,
  onClose
}: Props) {
  const { user } = useAuth();
  const isAdmin = user?.view_type === ViewType.Admin;

  // V4.9.8 — Build the actual list of allowed options for THIS ticket.
  // 1. Start from the flow that matches the ticket's category.
  // 2. If the user isn't Admin, strip the Approved-* statuses.
  const allowedStatuses = useMemo(() => {
    const flow = statusesForCategory(ticketCategory);
    if (isAdmin) return flow;
    return flow.filter((s) => !ADMIN_ONLY_STATUSES.includes(s));
  }, [ticketCategory, isAdmin]);

  const [status, setStatus] = useState<TicketStatus>(
    nextAllowed(currentStatus, ticketCategory, allowedStatuses)
  );
  const [note, setNote] = useState('');
  // V4.9.10 — tracking_number field, shown only when transitioning to a
  // Shipped status. Required by the backend for those transitions.
  const [trackingNumber, setTrackingNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const requiresTracking = STATUSES_REQUIRING_TRACKING_NUMBER.includes(status);

  const mutation = useMutation({
    mutationFn: async () => {
      // V4.9.10 — Friendly client-side validation before hitting the
      // backend (which will also reject). Saves a round-trip.
      if (requiresTracking && !trackingNumber.trim()) {
        throw new Error(`This status requires a tracking number — please enter it before saving.`);
      }
      const res = await ticketsApi.updateStatus(ticketId, {
        new_status: status,
        note: note || undefined,
        tracking_number: requiresTracking ? trackingNumber.trim() : undefined
      });
      // V4.9.13 — fire any teammate tags after the status change saves.
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: () => {
      push({ kind: 'success', title: 'Status updated' });
      invalidateAfterTicketAction(queryClient, ticketId);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError
          ? err.problem?.detail ?? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to update status'
      );
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change status"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving...' : 'Update status'}
          </button>
        </>
      }
    >
      <Field
        label="New status"
        required
        hint={
          ticketCategory.toUpperCase().startsWith('B')
            ? 'Showing B-flow statuses (sales / inquiry / bug workflow).'
            : isAdmin
              ? 'Showing all A-flow statuses (paid-order incident workflow).'
              : 'Approval statuses are Admin-only and hidden from this list.'
        }
      >
        <select
          className="form-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as TicketStatus)}
        >
          {allowedStatuses.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
      </Field>

      {/* V4.9.10 — Tracking number field. Appears ONLY when transitioning
         to "Reprint Shipped" or "Replacement Shipped". Solver MUST fill
         it so the communicator can include it in the customer email. */}
      {requiresTracking ? (
        <Field
          label="Tracking number"
          required
          hint="Required for Shipped statuses. The communicator will include this in the customer notification email."
        >
          <input
            type="text"
            className="form-input"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder="e.g. 1Z999AA10123456784, JD000666666669999999"
            autoFocus
          />
        </Field>
      ) : null}

      <Field label="Note (optional)">
        <textarea
          className="form-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
        />
      </Field>
      {tag.render()}
      {error ? <div className="form-error">{error}</div> : null}
    </Modal>
  );
}

/**
 * V4.9.8 — Suggest the next sensible status when the modal opens, scoped
 * to the actual allowed list for this user × category combination. Never
 * auto-picks an option the user can't even see.
 */
function nextAllowed(
  current: TicketStatus,
  category: string,
  allowed: TicketStatus[]
): TicketStatus {
  const isB = category.toUpperCase().startsWith('B');

  if (isB) {
    // V4.9.23 — B-flow progression: Open → In progress → Inform Customer → Completed.
    // (Fixes stale references to AwaitingCustomerReply/ResolvedCustomerAccepted,
    // which were removed from the B palette in V4.9.10 — those dead guards made
    // the modal default a non-Open B ticket back to "Open".)
    if (current === TicketStatus.Open && allowed.includes(TicketStatus.BInProgress)) {
      return TicketStatus.BInProgress;
    }
    if (current === TicketStatus.BInProgress && allowed.includes(TicketStatus.InformCustomer)) {
      return TicketStatus.InformCustomer;
    }
    if (current === TicketStatus.InformCustomer && allowed.includes(TicketStatus.Completed)) {
      return TicketStatus.Completed;
    }
    // No forward step mapped (e.g. already Completed/Cancelled) → pre-select the
    // current status (a no-op) rather than jumping backward to Open.
    return allowed.includes(current) ? current : allowed[0] ?? current;
  }

  // A-flow progression
  if (current === TicketStatus.Open && allowed.includes(TicketStatus.InvestigationInProgress)) {
    return TicketStatus.InvestigationInProgress;
  }
  if (
    current === TicketStatus.InvestigationInProgress &&
    allowed.includes(TicketStatus.InvestigationAwaitingApproval)
  ) {
    return TicketStatus.InvestigationAwaitingApproval;
  }
  if (
    current === TicketStatus.InvestigationAwaitingApproval &&
    allowed.includes(TicketStatus.ApprovedForReplacement)
  ) {
    return TicketStatus.ApprovedForReplacement;
  }
  return allowed[0] ?? current;
}
